import { registerLocaleVariant } from "../../registry";
import { makeId } from "../../helpers";
import type { SupplementBody } from "../../types";

// ─── Helper ─────────────────────────────────────────────────────────

function frSupplement(
  slug: string,
  variant: {
    title: string;
    body: Partial<SupplementBody>;
  },
): void {
  registerLocaleVariant(makeId(slug), "fr", variant);
}

// ─── Her supplements ────────────────────────────────────────────────

frSupplement("supplement-coq10", {
  title: "CoQ10 (Ubiquinol)",
  body: {
    name: "CoQ10 / Ubiquinol",
    dosageRange: "400 à 600 mg par jour",
    dailyTiming:
      "Prends-le avec un repas contenant des matières grasses. Fractionne les doses élevées entre matin et soir si besoin pour la tolérance.",
    timing: "Commence 3 mois avant le cycle prévu",
    bioavailability: {
      form: "L'ubiquinol est la forme réduite, souvent présentée comme plus facile à absorber que l'ubiquinone, surtout à doses élevées.",
      absorption:
        "La CoQ10 est liposoluble, donc l'absorption est meilleure avec un repas contenant des matières grasses.",
      timing:
        "Fractionne les doses quotidiennes élevées si elles te dérangent l'estomac ou si ta soignante préfère un apport plus régulier.",
    },
    foods: ["Abats", "Sardines", "Brocoli", "Chou-fleur"],
    expectations: {
      canExpect: [
        "Soutient l'énergie mitochondriale dans les ovocytes en développement",
        "Peut améliorer la qualité ovocytaire et le développement embryonnaire",
        "Bien toléré, peu d'effets secondaires",
      ],
      shouldNotExpect: [
        "Compte 3 mois — il faut un cycle complet de maturation ovocytaire pour voir les effets",
        "Ne va pas augmenter ton nombre d'ovocytes — il soutient la qualité de ceux que tu as",
        "Vient en soutien, pas en remplacement d'un traitement médical de fertilité",
      ],
    },
  },
});

frSupplement("supplement-vitamin-d", {
  title: "Vitamine D",
  body: {
    name: "Vitamine D3",
    dosageRange: "2 000 à 4 000 UI par jour",
    dailyTiming:
      "Prends-la avec un repas contenant des matières grasses. Le matin ou en milieu de journée est plus simple pour la plupart des gens, mais la régularité compte plus que l'heure exacte.",
    timing: "Commence tout de suite — la plupart des gens sont carencés",
    bioavailability: {
      form: "La vitamine D3 est généralement préférée à la D2 pour faire monter le taux sanguin de 25(OH)D.",
      absorption:
        "Elle est liposoluble et s'absorbe mieux avec un repas contenant des matières grasses.",
      metabolism:
        "Le taux sanguin, le poids corporel, le niveau de carence de départ et certains médicaments peuvent modifier la dose dont tu as besoin.",
      cautions: [
        "Évite de cumuler plusieurs produits à forte dose de vitamine D sans vérifier ton taux sanguin.",
      ],
    },
    foods: ["Poissons gras", "Jaunes d'œufs", "Lait enrichi", "Champignons"],
    expectations: {
      canExpect: [
        "Associée à de meilleurs résultats de FIV et taux d'implantation",
        "Soutient l'immunité pendant la grossesse",
        "Peu chère et largement disponible",
      ],
      shouldNotExpect: [
        "Elle corrige une carence, pas un nombre d'ovocytes bas — ce n'est pas un traitement de la réserve ovarienne",
        "Fais doser ton taux de base d'abord — la dose dépend de ton niveau actuel",
      ],
    },
  },
});

frSupplement("supplement-omega-3", {
  title: "Oméga-3 (EPA/DHA)",
  body: {
    name: "Acides gras oméga-3 (EPA/DHA)",
    dosageRange: "1 à 2 g d'EPA/DHA combinés par jour",
    dailyTiming:
      "Prends-les avec un repas contenant des matières grasses pour améliorer l'absorption et réduire les renvois de poisson.",
    timing: "Commence 3 mois avant le cycle prévu",
    bioavailability: {
      form: "Les huiles de poisson sous forme triglycéride ou triglycéride ré-estérifié sont souvent mieux absorbées que les formes éthyl ester.",
      absorption:
        "Prends-les avec un repas, idéalement contenant des matières grasses, pour améliorer l'absorption et réduire les renvois de poisson.",
      cautions: [
        "Vérifie la quantité d'EPA/DHA et pas seulement le total d'huile de poisson, et évite les produits rances.",
      ],
    },
    foods: ["Saumon", "Sardines", "Noix", "Graines de lin"],
    expectations: {
      canExpect: [
        "Effets anti-inflammatoires qui soutiennent la santé reproductive",
        "Peut améliorer la qualité ovocytaire et spermatique",
        "Bénéfique pour la santé cardiovasculaire et cérébrale globale",
      ],
      shouldNotExpect: [
        "Peu probable de changer radicalement les résultats des examens de fertilité à eux seuls",
        "Marchent mieux en accompagnement d'une alimentation équilibrée, pas en remplacement",
      ],
    },
  },
});

frSupplement("supplement-methylfolate", {
  title: "Méthylfolate",
  body: {
    name: "Méthylfolate (L-5-MTHF)",
    dosageRange: "800 à 1 000 µg par jour",
    dailyTiming:
      "Prends-le à la même heure chaque jour, avec ou sans nourriture. Le matin peut être plus simple si les vitamines B te donnent un coup de fouet.",
    timing: "Commence au moins 1 mois avant les essais de conception",
    bioavailability: {
      form: "Le L-5-MTHF est la forme active du folate : ton corps n'a pas besoin de le transformer avant de l'utiliser.",
      metabolism:
        "C'est utile si ton corps a du mal à transformer l'acide folique — mais prendre plus n'est pas forcément mieux.",
      cautions: [
        "Évite de cumuler des hautes doses de folate entre prénatal, complexe B et complément seul, sauf si ta soignante le souhaite.",
      ],
    },
    foods: [
      "Légumes à feuilles vertes",
      "Légumineuses",
      "Agrumes",
      "Céréales enrichies",
    ],
    expectations: {
      canExpect: [
        "Prévient les anomalies du tube neural — c'est la principale raison de le prendre",
        "Si ton corps transforme mal l'acide folique (variants MTHFR), le méthylfolate est déjà sous forme active et n'a pas besoin d'être converti (effet mécanistique ; les données d'efficacité directe restent limitées)",
        "Soutient la synthèse de l'ADN dans les cellules à division rapide",
      ],
      shouldNotExpect: [
        "Ne va pas améliorer le nombre ou la qualité de tes ovocytes — son rôle est de prévenir les anomalies du tube neural",
        "Si tu as déjà assez de folate par l'alimentation, le bénéfice supplémentaire est minime",
        "Aucun essai clinique rigoureux n'a comparé directement le L-5-MTHF à l'acide folique chez les porteuses de variants MTHFR : le choix repose sur le mécanisme, pas sur un bénéfice clair sur les naissances vivantes",
      ],
    },
  },
});

frSupplement("supplement-prenatal", {
  title: "Vitamines prénatales",
  body: {
    name: "Prénatal + Méthylfolate",
    dosageRange: "1 par jour — vise 800 à 1 000 µg de méthylfolate",
    dailyTiming:
      "Prends-le avec de la nourriture si ça cause des nausées. Espace-le d'au moins 4 heures de la lévothyroxine et sépare-le de la tétracycline ou des antibiotiques de la famille des quinolones, comme indiqué.",
    timing:
      "Commence au moins 3 mois avant la conception ou le traitement de fertilité",
    bioavailability: {
      form: "Cherche un prénatal qui indique clairement la forme et la dose de folate, l'iode sous une forme prévisible comme l'iodure de potassium, et le fer sous une forme tolérable si tu en as besoin.",
      absorption:
        "Le fer, le calcium, le magnésium et le zinc peuvent se concurrencer entre eux et avec certains médicaments. Prendre le prénatal avec de la nourriture peut réduire les nausées, mais le café, le thé, les repas riches en calcium et les antiacides peuvent réduire l'absorption du fer.",
      metabolism:
        "Le méthylfolate est déjà sous forme active. Cela peut aider quand la conversion de l'acide folique est réduite, mais cela ne veut pas dire que des doses plus élevées de folate sont toujours meilleures.",
      timing:
        "Si tu prends de la lévothyroxine, prends le prénatal au moins 4 heures plus tard. Si tu prends de la doxycycline ou des antibiotiques similaires, sépare le prénatal de l'antibiotique selon les indications de ta prescriptrice.",
      cautions: [
        "Vérifie l'ensemble de tes compléments avant d'ajouter du fer, de l'iode, de la vitamine D, de la choline, des oméga-3 ou du méthylfolate séparément, pour ne pas doubler les doses par accident.",
      ],
    },
    foods: [
      "Légumes à feuilles vertes",
      "Légumineuses",
      "Céréales enrichies",
      "Agrumes",
    ],
    expectations: {
      canExpect: [
        "Prévient les anomalies du tube neural — critique dans les 28 premiers jours après la conception",
        "La forme méthylfolate contourne les variants du gène MTHFR (30 à 40 % des femmes)",
        "Constitue des réserves de nutriments (fer, D3, DHA, choline, iode, B12)",
      ],
      shouldNotExpect: [
        "Une amélioration du nombre ou de la qualité des ovocytes par le prénatal seul",
        "Tous les prénataux ne se valent pas — cherche des produits testés par tiers (NSF, USP)",
        "Des bénéfices de l'acide folique si tu as des variants MTHFR",
      ],
    },
  },
});

frSupplement("supplement-choline", {
  title: "Choline",
  body: {
    name: "Choline",
    dosageRange:
      "Vise 450 mg par jour pendant la grossesse, alimentation + compléments",
    dailyTiming:
      "Prends la choline en complément avec de la nourriture, surtout si elle cause des nausées ou des renvois de poisson.",
    timing:
      "Constitue tes apports avant la grossesse et tout au long des trimestres ; les preuves les plus solides portent sur la supplémentation au 3e trimestre",
    bioavailability: {
      form: "La choline alimentaire, la phosphatidylcholine et les sels de choline peuvent tous contribuer, mais les étiquettes affichent des quantités différentes de choline.",
      absorption:
        "Prends la choline en complément avec de la nourriture si elle cause des nausées ou des renvois de poisson.",
      metabolism:
        "Le métabolisme intestinal peut influencer une odeur corporelle de poisson à doses plus élevées, donc l'apport total compte.",
    },
    foods: ["Œufs", "Poulet", "Bœuf", "Lait", "Soja", "Cacahuètes"],
    expectations: {
      canExpect: [
        "Soutient le développement du cerveau et de la moelle épinière du fœtus",
        "Aide à combler un manque fréquent en nutrition prénatale, car beaucoup de prénataux contiennent peu ou pas de choline",
        "Fonctionne mieux quand l'apport alimentaire est aussi compté, car les œufs et les protéines animales peuvent en apporter des quantités significatives",
      ],
      shouldNotExpect: [
        "Ce n'est pas un complément pour la qualité ovocytaire ni un traitement de l'infertilité",
        "Plus n'est pas automatiquement mieux — les fortes doses peuvent causer des troubles digestifs, des sueurs ou une odeur corporelle de poisson",
        "Ne remplace pas un prénatal avec folate, iode, fer et vitamine D",
      ],
    },
  },
});

frSupplement("supplement-iodine", {
  title: "Iode",
  body: {
    name: "Iode",
    dosageRange:
      "150 µg par jour dans un prénatal est souvent recommandé ; l'apport total visé en grossesse est d'environ 220 µg/jour",
    dailyTiming:
      "Prends-le avec ton prénatal ou un repas. Évite les produits à base de varech à forte dose sauf si ta soignante les recommande.",
    timing:
      "Commence avant la conception si ton prénatal ne contient pas déjà de l'iode",
    bioavailability: {
      form: "L'iodure de potassium dans un prénatal est plus prévisible que les gélules de varech ou d'algues.",
      absorption:
        "L'iode est généralement bien absorbé, mais la dose dans les produits d'algues peut varier énormément.",
      metabolism:
        "Ta thyroïde utilise l'iode pour fabriquer l'hormone thyroïdienne, donc à la fois trop peu et trop peuvent poser problème.",
      cautions: [
        "Évite les produits à base de varech à forte dose si tu as une maladie thyroïdienne, sauf si ta soignante les recommande spécifiquement.",
      ],
    },
    foods: ["Sel iodé", "Produits laitiers", "Œufs", "Fruits de mer", "Algues"],
    expectations: {
      canExpect: [
        "Soutient la production d'hormones thyroïdiennes en début de grossesse",
        "Aide à couvrir les besoins du cerveau et du système nerveux du fœtus en iode",
        "Sert de repère important pour vérifier l'étiquette d'un prénatal, car tous n'en contiennent pas",
      ],
      shouldNotExpect: [
        "Il ne traitera pas une maladie thyroïdienne à lui seul — une TSH anormale ou des anticorps thyroïdiens demandent une prise en charge médicale",
        "Évite les compléments de varech ou d'algues à forte dose sauf si ta soignante les recommande spécifiquement",
        "Si tu prends déjà de la lévothyroxine ou as une maladie thyroïdienne, confirme la dose avec ta médecin",
      ],
    },
  },
});

frSupplement("supplement-calcium", {
  title: "Calcium",
  body: {
    name: "Calcium",
    dosageRange:
      "Vise 1 000 mg par jour au total entre alimentation et compléments ; ne complète que le manque",
    dailyTiming:
      "Prends le carbonate de calcium avec les repas ; le citrate de calcium peut être pris avec ou sans nourriture. Fractionne les doses élevées et sépare-le du fer, des médicaments thyroïdiens et de certains antibiotiques.",
    timing:
      "À utiliser quand l'apport alimentaire est faible, surtout avant et pendant la grossesse",
    bioavailability: {
      form: "Le citrate de calcium peut se prendre avec ou sans nourriture ; le carbonate de calcium s'absorbe mieux avec les repas.",
      absorption:
        "L'absorption est meilleure en petites doses, donc fractionne les apports importants.",
      timing:
        "Sépare le calcium du fer, des médicaments thyroïdiens et de certains antibiotiques, car il peut réduire leur absorption.",
    },
    foods: [
      "Yaourt",
      "Lait",
      "Fromage",
      "Laits végétaux enrichis",
      "Tofu",
      "Sardines",
    ],
    expectations: {
      canExpect: [
        "Soutient la santé osseuse de la maman et le développement osseux et dentaire du fœtus",
        "Aide à compléter un plan nutritionnel prénatal quand l'alimentation est pauvre en produits laitiers ou enrichis",
        "Peut se prendre séparément du prénatal si celui-ci ne contient pas assez de calcium",
      ],
      shouldNotExpect: [
        "Il n'améliore pas le nombre ni la qualité des ovocytes",
        "Ne prends pas le calcium en même temps que le fer si tu cherches à améliorer l'absorption du fer",
        "Les doses élevées en complément peuvent causer constipation ou risque de calculs rénaux chez les personnes sensibles",
      ],
    },
  },
});

// ─── His supplements ────────────────────────────────────────────────

frSupplement("supplement-zinc", {
  title: "Zinc",
  body: {
    name: "Zinc",
    dosageRange: "25 à 50 mg par jour",
    dailyTiming:
      "Prends-le avec de la nourriture pour réduire les nausées. Ne le prends pas en même temps que du fer ou du calcium à forte dose.",
    timing: "Commence 3 mois avant la conception prévue",
    bioavailability: {
      form: "Le picolinate, le citrate et le gluconate de zinc sont les formes courantes en complément.",
      absorption:
        "Prends-le avec de la nourriture si ça cause des nausées, mais évite de le prendre en même temps que du fer ou du calcium à forte dose.",
      metabolism:
        "Une supplémentation à forte dose et de longue durée peut faire baisser le statut en cuivre.",
      cautions: [
        "Ne cumule pas plusieurs produits contenant du zinc au-dessus de la plage recommandée sans suivi.",
      ],
    },
    foods: ["Huîtres", "Viande rouge", "Graines de courge", "Pois chiches"],
    expectations: {
      canExpect: [
        "Soutient une numération et une concentration spermatiques saines",
        "Essentiel à la production de testostérone",
        "Peut améliorer la mobilité et la morphologie spermatique",
      ],
      shouldNotExpect: [
        "Compte 3 mois — les spermatozoïdes mettent environ 72 jours à arriver à maturité",
        "Ne résoudra pas une infertilité de facteur masculin sévère à lui seul",
        "Si tes taux de zinc sont déjà normaux, une supplémentation supplémentaire apporte peu",
      ],
    },
  },
});

frSupplement("supplement-l-carnitine", {
  title: "L-Carnitine",
  body: {
    name: "L-Carnitine",
    dosageRange: "2 à 3 g par jour",
    dailyTiming:
      "Fractionne les doses au gramme dans la journée, idéalement avec les repas, pour améliorer la tolérance.",
    timing: "Commence 3 mois avant la conception prévue",
    bioavailability: {
      form: "Les études sur le sperme utilisent la L-carnitine, l'acétyl-L-carnitine, ou des combinaisons ; elles ne sont pas interchangeables milligramme pour milligramme.",
      absorption:
        "Fractionne les doses au gramme dans la journée pour améliorer la tolérance.",
      metabolism:
        "Les bactéries intestinales peuvent métaboliser la carnitine en TMAO, ce qui peut compter dans les discussions sur le risque cardiovasculaire.",
    },
    foods: ["Viande rouge", "Poulet", "Lait", "Avocat"],
    expectations: {
      canExpect: [
        "Soutient la mobilité spermatique — l'un des compléments les plus étudiés pour la fertilité masculine",
        "Apporte de l'énergie aux spermatozoïdes en développement",
        "Peut améliorer la concentration et la morphologie spermatique",
      ],
      shouldNotExpect: [
        "Compte 3 mois pour un effet complet — les améliorations sont progressives",
        "Ne résoudra pas une oligospermie sévère à elle seule — consulte une urologue",
        "Les doses élevées peuvent causer un léger inconfort digestif — commence bas et augmente",
      ],
    },
  },
});

frSupplement("supplement-selenium", {
  title: "Sélénium",
  body: {
    name: "Sélénium",
    dosageRange: "55 à 200 µg par jour",
    dailyTiming:
      "Prends-le une fois par jour avec de la nourriture. Évite de cumuler plusieurs sources de sélénium car la marge de sécurité est étroite.",
    timing: "Commence 3 mois avant la conception prévue",
    bioavailability: {
      form: "La sélénométhionine est couramment utilisée et bien absorbée ; les noix du Brésil sont variables et peuvent faire dépasser la dose.",
      absorption:
        "Le sélénium est généralement bien absorbé, donc le contrôle de la dose compte plus que les astuces d'absorption agressives.",
      cautions: [
        "Le sélénium a une marge de sécurité étroite. Évite de multiplier les sources à forte dose.",
      ],
    },
    foods: ["Noix du Brésil", "Thon", "Œufs", "Graines de tournesol"],
    expectations: {
      canExpect: [
        "Antioxydant impliqué dans la protection de l'ADN spermatique contre les dommages oxydatifs",
        "Peut améliorer la mobilité spermatique combiné à la vitamine E",
        "Soutient la santé reproductive globale",
      ],
      shouldNotExpect: [
        "Ne fais jamais de méga-dose — un excès de sélénium est toxique. Reste dans la plage recommandée",
        "Ne traitera pas l'infertilité à lui seul — au mieux dans un protocole plus large",
        "Les résultats prennent des mois d'apport régulier — pas de solution miracle",
      ],
    },
  },
});

// ─── Her additional supplements ─────────────────────────────────────

frSupplement("supplement-myo-inositol", {
  title: "Myo-inositol",
  body: {
    name: "Myo-inositol",
    dosageRange:
      "2 à 4 g par jour (souvent associé au D-chiro-inositol en ratio 40:1)",
    dailyTiming:
      "Fractionne la dose matin et soir. Poudre ou gélules conviennent si tu arrives à prendre la dose quotidienne complète de manière régulière.",
    timing: "Commence 3 mois avant le cycle prévu",
    bioavailability: {
      form: "Beaucoup de formules PMOS (ex SOPK) associent myo-inositol et D-chiro-inositol en ratio 40:1.",
      absorption:
        "La poudre comme les gélules fonctionnent ; l'enjeu principal est d'atteindre régulièrement les doses au gramme.",
      timing:
        "Fractionner la dose matin et soir peut améliorer la tolérance digestive et l'observance de la routine.",
    },
    foods: ["Agrumes", "Haricots", "Riz complet", "Melon cantaloup"],
    expectations: {
      canExpect: [
        "Améliore l'ovulation en PMOS (ex SOPK) — l'un des compléments les mieux étudiés pour ça",
        "Peut améliorer la qualité ovocytaire et la sensibilité à l'insuline",
        "Soutient des cycles menstruels réguliers",
      ],
      shouldNotExpect: [
        "Ne remplace pas la prise en charge médicale du PMOS (ex SOPK) — à utiliser en complément de ton traitement",
        "Si tu ovules régulièrement sans PMOS (ex SOPK), le bénéfice est minime",
        "Compte 2 à 3 mois pour la régulation des cycles — les changements sont progressifs",
      ],
    },
  },
});

frSupplement("supplement-vitamin-c", {
  title: "Vitamine C",
  body: {
    name: "Vitamine C",
    dosageRange: "500 à 1 000 mg par jour",
    dailyTiming:
      "Prends-la avec ou sans nourriture. Prends-la avec du fer ou un repas riche en fer végétal si l'objectif est d'améliorer l'absorption du fer non héminique.",
    timing: "Commence au moins 1 mois avant les essais de conception",
    bioavailability: {
      form: "L'acide ascorbique standard suffit généralement ; les formes tamponnées peuvent être plus douces pour les estomacs sujets au reflux.",
      absorption:
        "L'absorption sature à hautes doses, donc les très grandes prises uniques sont surtout excrétées.",
      timing:
        "Peut améliorer l'absorption du fer non héminique quand elle est prise avec du fer végétal ou des compléments de fer.",
    },
    foods: ["Poivrons", "Fraises", "Agrumes", "Kiwi"],
    expectations: {
      canExpect: [
        "Protection antioxydante pour l'ADN des ovocytes et des spermatozoïdes",
        "Peut réduire la fragmentation de l'ADN spermatique chez l'homme",
        "Peut soutenir la progestérone chez les femmes avec un déficit en phase lutéale (preuves limitées)",
      ],
      shouldNotExpect: [
        "Peu probable d'améliorer radicalement la fertilité à elle seule",
        "Les doses au-dessus de 1 000 mg sont simplement excrétées — plus n'est pas mieux",
        "Ne remplace pas le traitement des conditions de fertilité sous-jacentes",
      ],
    },
  },
});

frSupplement("supplement-dhea", {
  title: "DHEA",
  body: {
    name: "DHEA (déhydroépiandrostérone)",
    dosageRange: "25 à 75 mg par jour (typiquement 25 mg 3 fois par jour)",
    dailyTiming:
      "À utiliser uniquement selon le protocole de ta clinique. Les cliniques fractionnent souvent la dose dans la journée et surveillent DHEA-S et testostérone.",
    timing:
      "Commence 6 à 8 semaines avant le cycle prévu — nécessite un suivi médical",
    bioavailability: {
      form: "C'est un précurseur hormonal, pas une vitamine ; la puissance du produit et la dose réelle comptent.",
      metabolism:
        "La DHEA peut se convertir en aval en androgènes et œstrogènes, donc la réponse varie selon les taux hormonaux de base.",
      timing:
        "Les cliniques fractionnent souvent la dose dans la journée et surveillent DHEA-S et testostérone.",
      cautions: [
        "Ne te lance pas en solo si tu as un PMOS (ex SOPK), des androgènes élevés, de l'acné, une pilosité accrue, ou des conditions hormono-sensibles.",
      ],
    },
    foods: [],
    expectations: {
      canExpect: [
        "Peut améliorer la réponse ovarienne chez les femmes avec réserve ovarienne diminuée",
        "Peut augmenter le nombre d'ovocytes obtenus pendant la stimulation de FIV",
        "Des études montrent de meilleurs taux de grossesse chez les mauvaises répondeuses",
      ],
      shouldNotExpect: [
        "Bénéfique uniquement avec un diagnostic de réserve ovarienne diminuée",
        "C'est une hormone, pas une vitamine — nécessite un suivi médical",
        "Tes taux de testostérone et de DHEA-S doivent être suivis pendant l'utilisation",
        "Les preuves sont devenues plus mitigées — la revue Cochrane 2024 conclut que la DHEA ne change probablement pas grand-chose au taux de naissances vivantes, alors que la testostérone pourrait aider les mauvaises répondeuses",
      ],
    },
  },
});

frSupplement("supplement-vitamin-e", {
  title: "Vitamine E",
  body: {
    name: "Vitamine E",
    dosageRange: "200 à 400 UI par jour",
    dailyTiming:
      "Prends-la avec un repas contenant des matières grasses. Évite d'ajouter de la vitamine E à forte dose près d'une intervention sauf accord de ta clinique.",
    timing: "Commence 2 à 3 mois avant le cycle prévu",
    bioavailability: {
      form: "Les tocophérols mixtes naturels peuvent différer des produits à base d'alpha-tocophérol synthétique.",
      absorption:
        "La vitamine E est liposoluble et s'absorbe mieux avec un repas contenant des matières grasses.",
      cautions: [
        "Les fortes doses de vitamine E peuvent augmenter le risque hémorragique, surtout autour des interventions ou sous anticoagulants.",
      ],
    },
    foods: ["Amandes", "Graines de tournesol", "Avocat", "Épinards"],
    expectations: {
      canExpect: [
        "Antioxydant impliqué dans la protection des ovocytes et spermatozoïdes contre les dommages oxydatifs",
        "Peut améliorer l'épaisseur endométriale chez la femme",
        "Synergique avec le sélénium pour la santé spermatique chez l'homme",
      ],
      shouldNotExpect: [
        "Les fortes doses peuvent être nocives — reste dans la plage recommandée de 200 à 400 UI",
        "Ne traitera pas les problèmes de fertilité à elle seule — au mieux dans un protocole plus large",
        "Plus efficace combinée à d'autres changements de mode de vie et alimentaires",
      ],
    },
  },
});

frSupplement("supplement-nac", {
  title: "NAC (N-acétylcystéine)",
  body: {
    name: "NAC (N-acétylcystéine)",
    dosageRange: "600 à 1 200 mg par jour",
    dailyTiming:
      "Peut se prendre avec ou sans nourriture. Prends-la avec de la nourriture si ça cause des nausées.",
    timing: "Commence 3 mois avant le cycle prévu",
    bioavailability: {
      form: "La NAC fournit de la cystéine, un précurseur du glutathion, plutôt que d'agir comme glutathion elle-même.",
      absorption:
        "Peut se prendre avec ou sans nourriture ; la nourriture peut réduire les nausées chez les estomacs sensibles.",
      metabolism:
        "Agit par des voies antioxydantes et fluidifiantes pour le mucus, ce qui est différent d'un traitement hormonal direct.",
    },
    foods: ["Poulet", "Dinde", "Ail", "Oignons", "Légumes crucifères"],
    expectations: {
      canExpect: [
        "Apporte de la cystéine, un précurseur du glutathion",
        "Peut améliorer les taux d'ovulation chez les femmes avec PMOS (ex SOPK)",
        "Soutient la qualité ovocytaire en réduisant le stress oxydatif",
      ],
      shouldNotExpect: [
        "Ne remplace pas les médicaments prescrits pour le PMOS (ex SOPK) comme la metformine",
        "Si tu ovules déjà normalement, le bénéfice est minime",
        "Les effets antioxydants se construisent progressivement sur des mois — pas de résultat immédiat",
      ],
    },
  },
});

frSupplement("supplement-melatonin", {
  title: "Mélatonine",
  body: {
    name: "Mélatonine",
    dosageRange: "3 mg par jour (au coucher)",
    dailyTiming:
      "Prends-la près du coucher et garde un horaire constant. Évite de la prendre dans la journée car elle peut causer de la somnolence.",
    timing: "Commence 1 à 2 mois avant le cycle de FIV",
    bioavailability: {
      form: "La mélatonine à libération immédiate est généralement utilisée pour la prise au coucher ; les produits à libération prolongée se comportent différemment.",
      absorption:
        "Elle s'absorbe rapidement et peut causer un coup de mou le lendemain chez certaines personnes.",
      timing:
        "Prends-la près du coucher et garde un horaire constant, car l'exposition à la lumière et le rythme circadien comptent.",
      cautions: [
        "Confirme avec ta clinique s'il faut l'arrêter avant le transfert ou la grossesse.",
      ],
    },
    foods: ["Cerises acidulées", "Pistaches", "Lait", "Baies de goji"],
    expectations: {
      canExpect: [
        "Un antioxydant mesuré dans le liquide folliculaire",
        "Peut améliorer la qualité ovocytaire pendant la stimulation de FIV",
        "Peut soutenir les horaires de sommeil, qui peuvent influencer les hormones reproductives",
      ],
      shouldNotExpect: [
        "Les preuves sont surtout spécifiques à la FIV — pas un complément de fertilité général",
        "Des doses plus élevées ne sont pas meilleures — 3 mg est la dose efficace étudiée",
        "Discutes-en avec ta médecin avant de poursuivre l'utilisation pendant la grossesse",
      ],
    },
  },
});

frSupplement("supplement-iron", {
  title: "Fer",
  body: {
    name: "Fer (bisglycinate ferreux)",
    dosageRange: "18 à 27 mg par jour (ou selon les résultats sanguins)",
    dailyTiming:
      "Prends-le à distance du calcium, du café, du thé, des antiacides et des médicaments thyroïdiens. La vitamine C peut améliorer l'absorption ; la nourriture peut réduire les nausées mais peut diminuer l'absorption.",
    timing: "Uniquement en cas de carence — fais d'abord doser ta ferritine",
    bioavailability: {
      form: "Le bisglycinate ferreux est souvent plus doux ; le sulfate ferreux est courant mais peut causer plus de constipation.",
      absorption:
        "La vitamine C améliore l'absorption. Le calcium, le thé, le café et les antiacides peuvent la réduire.",
      timing:
        "Prends-le à distance des compléments de calcium, du calcium prénatal, des médicaments thyroïdiens, du café et du thé.",
      cautions: [
        "Ne traite qu'une carence confirmée, sauf si ta soignante a une autre raison.",
      ],
    },
    foods: ["Viande rouge", "Épinards", "Lentilles", "Chocolat noir"],
    expectations: {
      canExpect: [
        "Corrige l'anémie ferriprive — liée à l'infertilité ovulatoire",
        "Soutient un bon flux sanguin vers l'utérus et les follicules en développement",
        "La forme bisglycinate ferreux est plus douce pour l'estomac",
      ],
      shouldNotExpect: [
        "Ne te supplémente qu'avec une carence confirmée par prise de sang — l'excès de fer est nocif",
        "N'aidera pas la fertilité si ton fer est déjà normal",
        "Reconstituer les réserves de fer prend 3 à 6 mois — sois patiente",
      ],
    },
  },
});

frSupplement("supplement-probiotics", {
  title: "Probiotiques",
  body: {
    name: "Probiotiques (souches de Lactobacillus)",
    dosageRange: "10 à 50 milliards d'UFC par jour",
    dailyTiming:
      "Suis l'étiquette du produit car les souches diffèrent. Prends-les à un horaire constant et vérifie si le produit doit être réfrigéré.",
    timing: "Commence au moins 1 mois avant la conception prévue",
    bioavailability: {
      form: "Le choix des souches compte plus que le total d'UFC ; les données de fertilité et de microbiome vaginal se concentrent sur des produits à dominance de Lactobacillus.",
      absorption:
        "Les probiotiques ne fonctionnent pas comme les nutriments absorbés. La survie au stockage et à l'acidité gastrique compte.",
      cautions: [
        "Ne pars pas du principe qu'un probiotique générique produira le même effet que les souches étudiées.",
      ],
    },
    foods: ["Yaourt", "Kéfir", "Choucroute", "Kimchi"],
    expectations: {
      canExpect: [
        "Un microbiome vaginal dominé par les Lactobacillus est associé à une meilleure implantation en FIV dans les études observationnelles",
        "Soutient la santé intestinale et l'absorption des nutriments",
      ],
      shouldNotExpect: [
        "Les probiotiques oraux n'ont pas démontré dans les essais cliniques qu'ils amélioraient les résultats de FIV — le lien microbiome-implantation vient de données observationnelles, pas d'essais de supplémentation",
        "Peu probable d'améliorer radicalement la fertilité à eux seuls",
        "Toutes les souches ne se valent pas — cherche spécifiquement des formules à dominance de Lactobacillus",
        "Pas un traitement des infections actives — consulte ta médecin pour ça",
      ],
    },
  },
});

frSupplement("supplement-magnesium", {
  title: "Magnésium",
  body: {
    name: "Magnésium (glycinate ou citrate)",
    dosageRange: "200 à 400 mg par jour",
    dailyTiming:
      "La prise en soirée est courante, surtout pour le glycinate. Fractionne la dose si elle cause des selles molles, et sépare-la des médicaments thyroïdiens ou de certains antibiotiques.",
    timing:
      "Commence à tout moment — beaucoup d'adultes consomment moins de magnésium que recommandé par l'alimentation",
    bioavailability: {
      form: "Le glycinate de magnésium est souvent plus doux ; le citrate peut ramollir les selles ; l'oxyde est mal absorbé chez beaucoup.",
      absorption:
        "Fractionne les doses si 400 mg en une fois cause diarrhée ou crampes.",
      timing:
        "Sépare-le des médicaments thyroïdiens et de certains antibiotiques car le magnésium peut réduire leur absorption.",
    },
    foods: ["Chocolat noir", "Amandes", "Épinards", "Graines de courge"],
    expectations: {
      canExpect: [
        "Soutient la production de progestérone et l'équilibre hormonal",
        "Peut réduire les symptômes de stress et améliorer le sommeil chez certaines personnes",
        "Peut réduire les crampes menstruelles et les symptômes du SPM",
      ],
      shouldNotExpect: [
        "Soutient la santé hormonale globale plutôt que de traiter directement la fertilité",
        "Toutes les formes ne se valent pas — choisis glycinate ou citrate pour une meilleure absorption",
        "Évite l'oxyde de magnésium — il est mal absorbé et peut causer des troubles digestifs",
      ],
    },
  },
});

// ─── Implantation & stress support ──────────────────────────────────

frSupplement("supplement-ashwagandha", {
  title: "Ashwagandha",
  body: {
    name: "Ashwagandha (KSM-66)",
    dosageRange: "300 à 600 mg par jour",
    dailyTiming:
      "Prends-la avec de la nourriture. La prise en soirée peut être préférable si elle a un effet sédatif ; le matin si elle a un effet stimulant.",
    timing:
      "Commence à tout moment — arrête avant le transfert d'embryon (discutes-en avec ta médecin)",
    bioavailability: {
      form: "Les extraits standardisés de racine comme KSM-66 ou Sensoril ne sont pas équivalents à la poudre générique.",
      absorption:
        "Prends-la avec de la nourriture si elle cause des troubles digestifs ou de la sédation.",
      metabolism:
        "Les extraits peuvent affecter les marqueurs thyroïdiens et les médicaments sédatifs chez certaines personnes.",
      cautions: [
        "À éviter pendant la grossesse et confirme le moment d'arrêt avant un transfert ou un cycle médicalisé.",
      ],
    },
    foods: [],
    expectations: {
      canExpect: [
        "Cliniquement prouvée pour baisser le cortisol et réduire le stress perçu",
        "Peut améliorer la numération et la mobilité spermatique chez les hommes sous stress",
        "Soutient la fonction thyroïdienne — importante pour l'équilibre hormonal",
      ],
      shouldNotExpect: [
        "Doit être arrêtée avant le transfert d'embryon — elle peut affecter l'implantation",
        "Pas sûre pendant la grossesse — à utiliser uniquement en phase pré-conception",
        "KSM-66 est la forme la plus étudiée — d'autres extraits peuvent différer en puissance",
      ],
    },
  },
});

frSupplement("supplement-vitamin-b-complex", {
  title: "Complexe de vitamines B",
  body: {
    name: "Complexe de vitamines B",
    dosageRange: "B6 : 25 à 50 mg, B12 : 500 à 1 000 µg par jour",
    dailyTiming:
      "Prends-le le matin ou tôt dans la journée, avec de la nourriture si ça cause des nausées.",
    timing: "Commence au moins 1 mois avant les essais de conception",
    bioavailability: {
      form: "Les formes méthylées de B12 et de folate peuvent être utiles pour certaines personnes, mais ne sont pas nécessaires pour tout le monde.",
      absorption:
        "Les vitamines B sont hydrosolubles, donc la régularité quotidienne compte plus qu'une grosse dose occasionnelle.",
      cautions: [
        "Surveille le total de B6 entre les produits, car une prise élevée et chronique peut causer des symptômes nerveux.",
      ],
    },
    foods: ["Œufs", "Saumon", "Légumes à feuilles vertes", "Pois chiches"],
    expectations: {
      canExpect: [
        "La B6 intervient dans le métabolisme des hormones stéroïdes, dont les voies de la progestérone",
        "La B12 soutient l'énergie, la méthylation et la fonction du système nerveux",
        "Peut aider à réguler les cycles et réduire les symptômes du SPM",
      ],
      shouldNotExpect: [
        "Les vitamines B sont hydrosolubles — elles demandent un apport quotidien régulier",
        "Si ton alimentation est déjà riche en B, la supplémentation peut ne pas apporter grand-chose",
        "Des doses élevées de B6 (au-dessus de 100 mg) peuvent causer des fourmillements — reste dans la plage",
        "Les taux de folate n'ont pas montré d'amélioration des taux de grossesse en FIV/PMA dans une méta-analyse récente (Paffoni 2022)",
      ],
    },
  },
});

frSupplement("supplement-l-arginine", {
  title: "L-Arginine",
  body: {
    name: "L-Arginine",
    dosageRange: "3 à 6 g par jour",
    dailyTiming:
      "Fractionne les doses au gramme dans la journée. Prends-la avec de la nourriture si ça cause un inconfort digestif.",
    timing: "Commence 1 à 2 mois avant le cycle prévu",
    bioavailability: {
      form: "La L-arginine est un précurseur du monoxyde d'azote ; la citrulline peut augmenter les taux d'arginine différemment, donc les preuves ne sont pas automatiquement interchangeables.",
      absorption:
        "Les doses au gramme sont souvent mieux tolérées en les fractionnant dans la journée.",
      metabolism:
        "Elle alimente les voies du monoxyde d'azote impliquées dans la vasodilatation plutôt que d'épaissir directement la muqueuse.",
      cautions: [
        "Évite d'en ajouter seule si tu prends des médicaments pour la tension, des nitrates, ou si tu fais des poussées d'herpès récurrentes, sans avis clinicien.",
      ],
    },
    foods: ["Dinde", "Graines de courge", "Pois chiches", "Lentilles"],
    expectations: {
      canExpect: [
        "Améliore le flux sanguin vers l'utérus — peut soutenir l'épaisseur endométriale",
        "Précurseur du monoxyde d'azote, qui détend les vaisseaux sanguins",
        "Certaines études montrent de meilleurs résultats de FIV chez les mauvaises répondeuses",
      ],
      shouldNotExpect: [
        "Les preuves sont encore émergentes — moins d'essais de grande taille que pour d'autres compléments",
        "Peut causer un inconfort digestif à doses plus élevées — commence à 3 g et augmente",
        "Pas un substitut à une évaluation médicale d'un endomètre fin",
      ],
    },
  },
});

// ─── Emerging / Newer supplements ───────────────────────────────────

frSupplement("supplement-creatine", {
  title: "Créatine",
  body: {
    name: "Créatine (monohydrate)",
    dosageRange: "3 à 5 g par jour",
    dailyTiming:
      "Prends-la une fois par jour à un horaire constant. La prendre avec un repas peut faciliter la régularité et réduire les troubles d'estomac.",
    timing: "Commence 3 mois avant le cycle prévu",
    bioavailability: {
      form: "Le monohydrate de créatine est la forme la mieux étudiée ; la plupart des formes spécialisées ont des preuves plus faibles.",
      absorption:
        "La régularité quotidienne compte plus qu'une phase de charge pour cet usage.",
      metabolism:
        "La créatine soutient le tampon énergétique cellulaire, mais les données spécifiques au métabolisme dans la fertilité sont encore préliminaires.",
    },
    foods: ["Viande rouge", "Poisson", "Volaille"],
    expectations: {
      canExpect: [
        "Soutient la production d'énergie mitochondriale dans les ovocytes et spermatozoïdes",
        "Une créatine plus élevée dans le liquide folliculaire est liée à une meilleure formation de blastocystes",
        "Peut soutenir la régularité menstruelle via une meilleure énergie cellulaire",
        "Bien étudiée pour la sécurité en santé générale",
      ],
      shouldNotExpect: [
        "Les essais cliniques spécifiques à la fertilité manquent encore — les preuves actuelles sont observationnelles et animales",
        "Le dosage pour le fitness général peut différer du dosage pour la santé reproductive",
        "Une certaine rétention d'eau est normale et sans gravité",
      ],
    },
  },
});

frSupplement("supplement-alpha-lipoic-acid", {
  title: "Acide alpha-lipoïque (ALA)",
  body: {
    name: "Acide alpha-lipoïque",
    dosageRange: "300 à 600 mg par jour",
    dailyTiming:
      "Souvent pris à distance des repas pour l'absorption, mais prends-le avec de la nourriture si ça cause des nausées. Prudence si tu prends un médicament ou complément hypoglycémiant.",
    timing: "Commence 3 mois avant le cycle prévu",
    bioavailability: {
      form: "L'acide R-lipoïque et les formes stabilisées peuvent différer de l'acide alpha-lipoïque standard, mais les essais de fertilité utilisent souvent des produits mixtes.",
      absorption:
        "L'absorption peut être plus élevée à distance des repas, mais la nourriture peut améliorer la tolérance.",
      metabolism:
        "Peut faire baisser la glycémie, ce qui compte s'il est combiné à la metformine, à l'inositol, à la berbérine ou à des antidiabétiques.",
    },
    foods: ["Épinards", "Brocoli", "Abats", "Tomates"],
    expectations: {
      canExpect: [
        "Antioxydant puissant qui protège les ovocytes des dommages oxydatifs",
        "Peut améliorer la sensibilité à l'insuline — particulièrement pertinent en PMOS (ex SOPK)",
        "Combiné au myo-inositol, peut augmenter le nombre d'ovocytes matures obtenus en FIV",
        "Réduit les marqueurs inflammatoires en inhibant la voie NF-κB",
      ],
      shouldNotExpect: [
        "La plupart des études combinent l'ALA à d'autres compléments, ce qui rend difficile d'isoler son effet individuel",
        "Ce n'est pas un traitement autonome du PMOS (ex SOPK) — il complète les soins médicaux",
        "Peut faire baisser la glycémie, donc surveille tes taux si tu prends un antidiabétique",
      ],
    },
  },
});

frSupplement("supplement-resveratrol", {
  title: "Resvératrol",
  body: {
    name: "Resvératrol",
    dosageRange: "100 à 200 mg par jour",
    dailyTiming:
      "Prends-le avec de la nourriture pour la tolérance. Arrête-le pendant la phase lutéale, la fenêtre de transfert d'embryon et la grossesse, sauf avis contraire de ta soignante.",
    timing:
      "Commence 3 mois avant le cycle — arrête pendant la phase lutéale et la grossesse",
    bioavailability: {
      form: "Le trans-resvératrol est la forme généralement mise en avant sur les étiquettes, mais la biodisponibilité humaine reste limitée.",
      absorption:
        "Le resvératrol est rapidement métabolisé, donc une dose élevée sur l'étiquette ne signifie pas une forte exposition tissulaire active.",
      metabolism:
        "Il peut influencer la signalisation des œstrogènes et les voies de décidualisation, raison pour laquelle le moment d'arrêt compte.",
      cautions: [
        "Ne le continue pas pendant la phase lutéale, le transfert d'embryon ou la grossesse, sauf indication spécifique de ta soignante.",
      ],
    },
    foods: ["Raisin rouge", "Myrtilles", "Chocolat noir", "Cacahuètes"],
    expectations: {
      canExpect: [
        "Active SIRT1, une molécule liée au ralentissement du vieillissement ovarien",
        "Des études animales montrent une amélioration de la qualité ovocytaire, de l'implantation et des taux de descendance vivante chez des modèles vieillissants",
        "Plausibilité mécanistique contre le déclin de fertilité lié à l'âge",
      ],
      shouldNotExpect: [
        "Aucun essai clinique humain n'a montré d'amélioration de la qualité ovocytaire, de la fécondation ou des naissances vivantes — le bénéfice sur les ovocytes repose sur des études chez la souris et sur le mécanisme, pas sur des essais cliniques",
        "Évite l'usage pendant la phase lutéale — des études en laboratoire montrent que le resvératrol peut perturber la décidualisation de l'endomètre, qui est nécessaire à l'implantation (Ochiai 2019)",
        "Évite pendant la grossesse sauf si ta soignante le recommande spécifiquement",
        "Ce n'est pas un traitement anti-âge prouvé — les preuves sont encore émergentes",
      ],
    },
  },
});

frSupplement("supplement-curcumin", {
  title: "Curcumine (curcuma)",
  body: {
    name: "Curcumine / extrait de curcuma",
    dosageRange: "500 à 1 000 mg par jour (forme biodisponible)",
    dailyTiming:
      "Prends-la avec de la nourriture, surtout un repas contenant des matières grasses, sauf indication contraire de l'étiquette. Les produits contenant de la pipérine ont un risque d'interaction plus élevé.",
    timing:
      "Peut être commencée à tout moment — utilise une formulation biodisponible (avec pipérine ou liposomale)",
    bioavailability: {
      form: "La curcumine pure a une très faible biodisponibilité ; les produits phytosome, liposomaux ou avec pipérine sont conçus pour augmenter l'exposition.",
      absorption:
        "Prends-la avec de la nourriture, surtout des repas contenant des matières grasses, sauf indication contraire de l'étiquette.",
      metabolism:
        "La pipérine peut modifier le métabolisme des médicaments, donc une formule « plus biodisponible » peut aussi signifier plus de risque d'interaction.",
      cautions: [
        "Prudence renforcée avec les anticoagulants, les médicaments de FIV, et le calendrier d'interventions ou de procédures.",
      ],
    },
    foods: ["Racine de curcuma", "Poudre de curry"],
    expectations: {
      canExpect: [
        "Propriétés anti-inflammatoires et antioxydantes fortes",
        "Peut aider à réduire l'inflammation liée à l'endométriose",
        "Quelques preuves pour la protection des spermatozoïdes contre les dommages oxydatifs",
        "Bien tolérée à doses standard",
      ],
      shouldNotExpect: [
        "Les résultats sur la douleur d'endométriose dans les essais randomisés sont mixtes",
        "La curcumine a une très faible biodisponibilité — la formulation compte beaucoup",
        "Les effets sur les spermatozoïdes sont inconsistants entre les études, avec certains résultats négatifs à fortes doses",
      ],
    },
  },
});

frSupplement("supplement-berberine", {
  title: "Berbérine",
  body: {
    name: "Berbérine",
    dosageRange: "500 mg deux fois par jour",
    dailyTiming:
      "Souvent prise avec les repas pour cibler la glycémie post-prandiale et réduire les effets secondaires digestifs. Ne la combine pas à la légère avec la metformine ou des antidiabétiques.",
    timing: "Commence 3 mois avant le cycle prévu",
    bioavailability: {
      form: "La berbérine a une faible biodisponibilité orale, mais elle peut quand même avoir des effets digestifs et métaboliques significatifs.",
      absorption:
        "Souvent prise avec les repas pour cibler la glycémie post-prandiale et réduire les effets secondaires digestifs.",
      metabolism:
        "Peut affecter les enzymes CYP et les transporteurs P-gp, donc les interactions médicamenteuses sont plausibles.",
      cautions: [
        "Ne la combine pas à la légère avec la metformine, des antidiabétiques ou des essais de grossesse sans avis clinicien.",
      ],
    },
    foods: [],
    expectations: {
      canExpect: [
        "Améliore la sensibilité à l'insuline via l'activation de l'AMPK — comparable à la metformine dans certaines études",
        "Peut restaurer une ovulation régulière en PMOS (ex SOPK) en réduisant l'insulinorésistance et les androgènes",
        "Chez les femmes PMOS (ex SOPK) en FIV, un prétraitement de 3 mois à la berbérine a amélioré le taux de naissance vivante par rapport à la metformine et réduit l'hyperstimulation ovarienne sévère (An 2014)",
        "Peut avoir des bénéfices cardiovasculaires au-delà de la metformine (amélioration du profil lipidique)",
      ],
      shouldNotExpect: [
        "Elle n'est pas approuvée par la FDA pour le PMOS (ex SOPK) — c'est un complément botanique, pas un médicament",
        "Les effets secondaires digestifs (nausées, crampes) sont fréquents, surtout à doses plus élevées",
        "Ne devrait pas être combinée avec la metformine sans suivi médical en raison de l'effet additif sur la glycémie",
      ],
    },
  },
});

// ─── Commonly marketed / evidence-caution supplements ────────────────

frSupplement("supplement-maca", {
  title: "Maca",
  body: {
    name: "Racine de maca",
    dosageRange: "Souvent commercialisée à 1,5 à 3 g par jour",
    dailyTiming:
      "Il n'y a pas de meilleur moment prouvé spécifique à la fertilité. Si tu l'utilises, prends-la avec de la nourriture et évite de l'ajouter pendant des cycles suivis sans prévenir ta clinique.",
    timing:
      "Si utilisée, les périodes d'essai sont généralement de 8 à 12 semaines ; arrête à la grossesse sauf avis contraire de ta soignante",
    bioavailability: {
      form: "La maca gélatinisée retire une partie de l'amidon et peut être plus facile à digérer, mais cela ne prouve pas de meilleurs résultats de fertilité.",
      absorption:
        "La plupart des allégations ne reposent pas sur un composé actif pour la fertilité dont l'absorption est connue.",
      metabolism:
        "Le marketing évoque souvent des effets hormonaux, mais les effets endocriniens chez l'humain ne sont pas établis de manière fiable.",
    },
    foods: [],
    expectations: {
      canExpect: [
        "Tu peux la voir commercialisée pour la libido, l'énergie et la qualité spermatique",
        "De petites études de facteur masculin ont examiné la concentration et la mobilité spermatique",
        "À considérer comme un adjuvant à faible certitude, pas un complément de fertilité central",
      ],
      shouldNotExpect: [
        "Une amélioration fiable des résultats de FIV, de la qualité ovocytaire ou des taux de grossesse",
        "Des bénéfices spermatiques constants — les revues systématiques décrivent des résultats mixtes",
        "Un substitut au spermogramme, aux changements de mode de vie ou à une évaluation urologique",
      ],
    },
  },
});

frSupplement("supplement-royal-jelly", {
  title: "Gelée royale",
  body: {
    name: "Gelée royale",
    dosageRange: "Pas de dose spécifique à la fertilité fondée sur des preuves",
    dailyTiming:
      "Il n'y a pas de meilleur moment prouvé spécifique à la fertilité. À éviter si tu es allergique aux produits de la ruche, sujette à l'asthme, ou enceinte, sauf accord de ta soignante.",
    timing:
      "Pas recommandée en routine ; à éviter si tu es allergique aux produits de la ruche, sujette à l'asthme ou enceinte, sauf accord de ta soignante",
    bioavailability: {
      form: "Les gélules, la gelée royale fraîche et les mélanges ne sont pas standardisés à une dose active pertinente pour la fertilité.",
      absorption:
        "Il n'y a pas de cible d'absorption validée qui se traduise par un bénéfice sur la qualité ovocytaire ou la FIV.",
      cautions: [
        "Le risque d'allergie est le principal enjeu lié à la biodisponibilité : un allergène absorbé peut être cliniquement dangereux.",
      ],
    },
    foods: [],
    expectations: {
      canExpect: [
        "Tu peux voir des allégations selon lesquelles elle améliore la qualité ovocytaire parce que les reines en mangent",
        "La plupart des données favorables sont animales ou des études humaines très limitées",
        "Cette fiche est incluse pour pouvoir évaluer en un même endroit les allégations de fertilité non étayées",
      ],
      shouldNotExpect: [
        "Une amélioration fiable de la réserve ovarienne, de la réponse en FIV, de la qualité ovocytaire ou des naissances vivantes",
        "Des bénéfices de fertilité humaine comparables à ceux de compléments établis comme le folate ou la vitamine D",
        "Un faible risque allergique — la gelée royale peut déclencher de graves réactions chez les personnes sensibles",
      ],
    },
  },
});

frSupplement("supplement-shatavari", {
  title: "Shatavari",
  body: {
    name: "Shatavari (Asparagus racemosus)",
    dosageRange: "Pas de dose standardisée pour la fertilité",
    dailyTiming:
      "Il n'y a pas de meilleur moment prouvé spécifique à la fertilité. Ne le superpose pas à un traitement hormonal sans avis clinicien.",
    timing:
      "Discute avec ta soignante avant utilisation, surtout si tu es sous hormones, si tu as une condition œstrogéno-sensible, ou si tu es enceinte",
    bioavailability: {
      form: "Les poudres, extraits et préparations traditionnelles peuvent différer substantiellement en concentration.",
      absorption:
        "Il n'y a pas de dose standardisée de fertilité ni de marqueur d'absorption validé pour comparer les produits.",
      metabolism:
        "Les effets possibles de type hormonal sont précisément la raison pour laquelle il ne faut pas le superposer à la légère aux médicaments de fertilité.",
    },
    foods: [],
    expectations: {
      canExpect: [
        "Tu peux le voir commercialisé en Ayurveda pour les symptômes reproductifs, les cycles et la libido",
        "Quelques études préliminaires explorent les effets en PMOS (ex SOPK) ou hormonaux, mais la qualité des preuves est limitée",
        "Plus pertinent comme sujet culturel ou de soin complémentaire que comme outil de fertilité prouvé",
      ],
      shouldNotExpect: [
        "Une amélioration cliniquement prouvée de l'ovulation, des résultats de FIV ou des naissances vivantes",
        "Des effets hormonaux prévisibles — les formulations et les doses varient largement",
        "Un remplacement d'un traitement fondé sur les preuves du PMOS (ex SOPK), de l'endométriose ou de l'infertilité",
      ],
    },
  },
});

frSupplement("supplement-vitex", {
  title: "Vitex",
  body: {
    name: "Vitex / gattilier",
    dosageRange: "Pas de dose standard spécifique à la fertilité",
    dailyTiming:
      "Il n'y a pas de meilleur moment prouvé spécifique à la fertilité. Évite d'en ajouter seule pendant des cycles médicalisés ou des essais de grossesse sans avis clinicien.",
    timing:
      "Ne le combine pas avec des hormones de fertilité, des médicaments liés à la dopamine, ou des essais de grossesse sans avis clinicien",
    bioavailability: {
      form: "Les extraits sont généralement standardisés différemment selon les marques, donc les comparaisons de dose sont compliquées.",
      metabolism:
        "Le vitex peut agir via la signalisation dopamine/prolactine plutôt que par une nutrition ovarienne directe.",
      timing:
        "Cette voie rend le timing risqué pendant les cycles médicalisés ou quand la prolactine, la phase lutéale ou des problèmes d'ovulation n'ont pas été évalués.",
    },
    foods: [],
    expectations: {
      canExpect: [
        "Tu peux le voir commercialisé pour le SPM, le soutien de la phase lutéale et la régulation du cycle",
        "Quelques données suggèrent des bénéfices liés à la prolactine ou au SPM, mais les preuves spécifiques à la fertilité sont faibles",
        "Peut être évoqué si les cycles sont irréguliers, mais ne devrait pas être ajouté seul pendant des cycles de traitement",
      ],
      shouldNotExpect: [
        "Une amélioration fiable de l'implantation, des résultats de FIV ou des naissances vivantes",
        "Un ajout inoffensif pendant des cycles médicalisés — il peut interagir avec la signalisation hormonale",
        "Un substitut au dosage de la prolactine, à l'évaluation thyroïdienne, ovulatoire ou de la phase lutéale",
      ],
    },
  },
});

frSupplement("supplement-nad-nmn", {
  title: "NAD+ / NMN",
  body: {
    name: "Précurseurs du NAD+ (NMN / NR)",
    dosageRange: "Pas de dose spécifique à la fertilité fondée sur des preuves",
    dailyTiming:
      "Il n'y a pas de meilleur moment prouvé spécifique à la fertilité. Si tu l'utilises, prends-le de manière régulière et évite-le pendant la grossesse sauf si ta soignante le recommande.",
    timing:
      "L'usage en recherche est encore en évolution ; à éviter pendant la grossesse sauf si ta soignante le recommande spécifiquement",
    bioavailability: {
      form: "Le NMN et le NR sont des précurseurs différents du NAD+ ; les données sur les ovocytes animaux ne prouvent pas des effets équivalents sur la fertilité humaine.",
      absorption:
        "Faire monter les métabolites liés au NAD ne signifie pas automatiquement une meilleure qualité ovocytaire ou plus de naissances vivantes.",
      metabolism:
        "Ces composés alimentent les voies cellulaires du NAD+, mais l'exposition tissulaire reproductive chez l'humain n'est pas bien définie.",
    },
    foods: [],
    expectations: {
      canExpect: [
        "Tu peux le voir commercialisé pour le vieillissement ovarien, la fonction mitochondriale et la qualité ovocytaire",
        "Des études animales suggèrent que la reconstitution du NAD+ peut améliorer la qualité ovocytaire avec l'âge",
        "Les essais humains de FIV émergent, mais l'usage de routine en fertilité n'est pas établi",
      ],
      shouldNotExpect: [
        "Une amélioration prouvée des taux de grossesse ou de naissance vivante",
        "Des recommandations claires sur la dose, la forme du produit ou la sécurité à long terme chez les patientes de fertilité",
        "Le même niveau de preuves que la CoQ10, la vitamine D, le folate ou l'inositol pour le PMOS (ex SOPK)",
      ],
    },
  },
});
